import { Col, Row } from "@amzn/stencil-react-components/layout";
import { RowAttributesFunction, Table, TableColumn, TableSpacing } from "@amzn/stencil-react-components/table";
import { Text } from "@amzn/stencil-react-components/text";
import IconCheckCircleFill from "@amzn/stencil-react-icons/icons/icon-check-circle-fill";
import IconClockFill from "@amzn/stencil-react-icons/icons/icon-clock-fill";
import React, { FC, useCallback } from "react";
import { Order } from "../../orders.types";
import { CancelOrderButton } from "../OrderCancellation/cancel-order-button";
import { useCancelOrderMutation } from "../OrderCancellation/use-cancel-order-mutation";
import { CancellationReason, extractOrderDetails } from "../OrderCancellation/types";
import { useNotificationStore } from "@/common/stores/use-notification-store";
import { formatPhoneNumber, canCancel } from "./hardware-orders-table.util";
import {
  OrdersLoadingView,
  OrdersErrorState,
  OrdersEmptyState,
  buildStatusLabel,
  buildDateComponent,
  messagesColumn,
} from "./hardware-orders-table.shared";

function buildOrderStatusComponent(value: string) {
  const iconComponent =
    value === "COMPLETED" || value === "DELIVERED" ? (
      <IconCheckCircleFill color="green70" />
    ) : (
      <IconClockFill color="pink70" />
    );
  return (
    <Row gridGap={8}>
      {iconComponent} {buildStatusLabel(value)}
    </Row>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const baseColumns: TableColumn<any>[] = [
  {
    header: "Order Number",
    accessor: ({ data }: { data: Order }) => data.orderReference ?? "-",
  },
  {
    header: "Name",
    accessor: ({ data }: { data: Order }) => (
      <Text fontSize="T200" color="neutral90" fontWeight={600}>
        {data.items?.[0]?.itemInformation?.name ?? "-"}
      </Text>
    ),
  },
  {
    header: "Status",
    accessor: ({ data }: { data: Order }) => buildOrderStatusComponent(data.status ?? "-"),
  },
  {
    header: "Order date",
    accessor: ({ data }: { data: Order }) => buildDateComponent(data.requested?.on),
  },
];

const tableColumns = [...baseColumns, messagesColumn];

const FREE_STRING = "Free";
const NOT_APPLICABLE = "N/A";

const formatPrice = (total?: { amount?: number; currency?: string }): string => {
  if (!total || total.amount === undefined || !total.currency) return NOT_APPLICABLE;
  return total.amount === 0 ? FREE_STRING : `${total.currency} ${total.amount}`;
};

const OrderDetailsSection: FC<{ order: Order }> = ({ order }) => {
  const item = order.items?.[0];
  const checkoutInfo = order.details?.checkoutInfo;
  return (
    <Col gridGap="S200" width="50%">
      <Text fontWeight="bold">Order details</Text>
      <Text color="neutral70">Version</Text>
      <Text>{item?.itemInformation?.name ?? NOT_APPLICABLE}</Text>
      <Text color="neutral70">Price</Text>
      <Text>{formatPrice(order.total)}</Text>
      <Text color="neutral70">Cost center</Text>
      <Text>{checkoutInfo?.costCenter || NOT_APPLICABLE}</Text>
      <Text color="neutral70">CAR code</Text>
      <Text>{checkoutInfo?.carCode || NOT_APPLICABLE}</Text>
      <Text color="neutral70">Business justification</Text>
      <Text>{checkoutInfo?.businessJustification || NOT_APPLICABLE}</Text>
      <Text color="neutral70">PO Number</Text>
      <Text>{item?.details?.poNumber || NOT_APPLICABLE}</Text>
      <Text color="neutral70">Return device serial number</Text>
      <Text>{item?.details?.returnedDeviceSerialNumber || NOT_APPLICABLE}</Text>
    </Col>
  );
};

const ShippingDetailsSection: FC<{ order: Order }> = ({ order }) => {
  const delivery = order.deliveryTo;
  const hop = order.items?.[0]?.details?.hopOrders?.[0];

  const requesterName = order.requester?.basicInfo
    ? `${order.requester.basicInfo.firstName ?? ""} ${order.requester.basicInfo.lastName ?? ""}`.trim()
    : NOT_APPLICABLE;

  const shippingAddress = delivery
    ? [
        delivery.addressLineData?.filter((line) => line?.trim()).join(", "),
        delivery.city?.trim() || null,
        delivery.county?.trim() || null,
        delivery.regionSubdivision1?.trim() || null,
        delivery.country?.trim() || null,
        delivery.postalCode?.trim() || null,
      ]
        .filter((field) => field?.toString().trim())
        .join(", ")
    : NOT_APPLICABLE;

  const phoneNumber = delivery?.phoneNumber ? formatPhoneNumber(delivery.phoneNumber) : NOT_APPLICABLE;

  return (
    <Col gridGap="S200" width="50%">
      <Text fontWeight="bold">Shipping details</Text>
      <Text color="neutral70">Recipient</Text>
      <Text>{requesterName}</Text>
      <Text color="neutral70">Shipping address</Text>
      <Text>{shippingAddress}</Text>
      <Text color="neutral70">Delivery instructions</Text>
      <Text>{delivery?.deliveryInstructions || NOT_APPLICABLE}</Text>
      <Text color="neutral70">Phone number</Text>
      <Text>{phoneNumber}</Text>
      <Text color="neutral70">Carrier</Text>
      <Text>{hop?.shippingCarrier || NOT_APPLICABLE}</Text>
      <Text color="neutral70">Tracking number</Text>
      <Text>{hop?.trackingNumber || NOT_APPLICABLE}</Text>
      <Text color="neutral70">Shipping order ID</Text>
      <Text>{hop?.identifier || NOT_APPLICABLE}</Text>
    </Col>
  );
};

interface LegacyHardwareOrdersTableProps {
  orders: Order[];
  isLoading: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

export const LegacyHardwareOrdersTable: FC<LegacyHardwareOrdersTableProps> = ({
  orders,
  isLoading,
  hasError = false,
  onRetry,
}) => {
  const hash = window.location.hash.replace("#", "");
  const cancelMutation = useCancelOrderMutation();
  const addBanner = useNotificationStore((state) => state.addBanner);

  const handleConfirmCancellation = useCallback(
    (orderId: string, reason: CancellationReason) => {
      cancelMutation.mutate(
        { orderId, reason },
        {
          onSuccess: (result) => {
            if (result.success) {
              addBanner({ type: "success", message: "Your order has been cancelled successfully." }, 5000);
            } else if (result.error) {
              addBanner({ type: "error", message: result.error });
            }
          },
          onError: () => {
            addBanner({ type: "error", message: "We couldn't cancel your order. Please try again or contact support." });
          },
        }
      );
    },
    [cancelMutation, addBanner]
  );

  const expandableContent = ({ data }: { data: Order; index: number }): React.ReactElement[] => {
    const content: React.ReactElement[] = [
      <Row key="details" gridGap="S400" style={{ width: "100%", padding: "16px 0" }}>
        <OrderDetailsSection order={data} />
        <div style={{ width: "1px", backgroundColor: "#e0e0e0", margin: "0 8px" }} />
        <ShippingDetailsSection order={data} />
      </Row>,
    ];

    if (canCancel(data)) {
      content.push(
        <Row key="cancel" style={{ width: "100%", paddingTop: "8px" }}>
          <CancelOrderButton
            orderStatus={data.status}
            orderId={data.orderId}
            orderDetails={extractOrderDetails(data)}
            cancellationState={{ isLoading: cancelMutation.isPending, error: null }}
            onConfirmCancellation={(reason) => handleConfirmCancellation(data.orderId, reason)}
            orderItems={data.items}
          />
        </Row>
      );
    }

    return content;
  };

  const getRowAttributes: RowAttributesFunction = ({ data }) => ({
    expandableContent,
    disabled: false,
    id: data.orderId,
    isDefaultExpanded: data.orderId === hash,
  });

  if (isLoading) {
    return <OrdersLoadingView />;
  }

  if (hasError) {
    return <OrdersErrorState onRetry={onRetry} />;
  }

  if (orders.length === 0) {
    return <OrdersEmptyState />;
  }

  return (
    <Table
      actionHeader="Detail"
      columns={tableColumns}
      data={orders}
      spacing={TableSpacing.Default}
      getRowAttributes={getRowAttributes}
    />
  );
};

export default LegacyHardwareOrdersTable;
